// src/hooks/useSheetTableFilters.js

import { useEffect, useMemo, useState } from 'react';
import { getRowDate } from '../utils/statistics';

/**
 * Shared search + date-range filtering and pagination for sheet table pages.
 */
export function useSheetTableFilters(data, { rowsPerPage = 15 } = {}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const allDates = useMemo(() => {
    const dates = new Set();
    data.forEach(row => {
      const d = getRowDate(row);
      if (d) dates.add(d);
    });
    return Array.from(dates).sort();
  }, [data]);

  const filteredData = useMemo(() => {
    let rows = [...data];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter(row =>
        Object.values(row).some(v => String(v).toLowerCase().includes(term))
      );
    }

    if (dateFrom || dateTo) {
      rows = rows.filter(row => {
        const d = getRowDate(row);
        if (!d) return false;
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
      });
    }

    return rows;
  }, [data, searchTerm, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, dateFrom, dateTo]);

  const clearFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return {
    searchTerm,
    setSearchTerm,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    page,
    setPage,
    allDates,
    filteredData,
    paginatedData,
    totalPages,
    clearFilters,
  };
}
